import { Building2, GraduationCap, Presentation, User } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { TFunction } from 'i18next'

import { formatTimelineAge } from '@/lib/date-utils'
import type { PortalContext } from '@/hooks/usePortalMe.types'

/**
 * PORT-005 §5.2 — **la ligne 1 d'un espace : son TYPE, traduit.**
 *
 * ⚠️ **Une seule fonction, parce que DEUX écrans montrent la même liste** — la
 * page de choix et le menu de l'en-tête. Écrites séparément, elles
 * divergeraient, et la personne qui a choisi « Espace client » retrouverait
 * autre chose dans son en-tête sans savoir si elle est au bon endroit.
 *
 * ⛔ **Le libellé ne vient PAS du serveur ici**, alors que le serveur en sert
 * un (`_nommage.py`, `TITRE_PAR_NATURE`) : celui-là habille l'**e-mail**, qui
 * est rendu côté serveur dans la langue de la fiche. Le sélecteur, lui, doit
 * suivre la langue de l'INTERFACE. Deux rendus d'un même vocabulaire, donc —
 * et c'est exactement ce qui diverge au premier ajout. Le gardien
 * `test_port005_le_selecteur_et_lemail_nomment_lespace_pareil.py` interdit
 * cette divergence en comparant les deux tables.
 */
const CLE_PAR_NATURE: Record<PortalContext['kind'], string> = {
    CONTACT: 'client',
    INSTRUCTOR: 'instructor',
    LEARNER: 'learner',
    PARTICULIER: 'mine',
}

export function cleDuTypeDEspace(kind: PortalContext['kind']): string {
    return `portal.context.kind.${CLE_PAR_NATURE[kind] ?? 'client'}`
}

/**
 * PORT-005 §5.3 (A18, décision PO du 2026-09-16) — **ce que l'en-tête dit de l'espace
 * en cours**, pour ses DEUX lecteurs : `PortalHeader` (zéro ou un espace) et le
 * déclencheur de `PortalContextSwitcher` (deux et plus).
 *
 * À zéro espace, la phrase du CORPS (`empty.title`, « Aucun espace ouvert ») : l'en-tête
 * disait « Aucun espace actif » au-dessus d'elle, deux phrases pour un seul état.
 * ⚠️ `none` reste JUSTE quand la personne a des espaces et n'est dans aucun. Filet
 * seulement : le serveur ne sert pas cet état (`_actor.py` annule le contexte actif sur
 * le critère même qui filtre la liste, et la coque redirige vers le choix).
 */
export function libelleDeLEspaceEnCours(
    contexteActif: Pick<PortalContext, 'kind'> | null,
    nombreDEspaces: number,
    t: TFunction,
): string {
    if (contexteActif) return t(cleDuTypeDEspace(contexteActif.kind))
    return nombreDEspaces === 0 ? t('portal.context.empty.title') : t('portal.context.none')
}

/**
 * PORT-005 §6 — l'icône de chaque nature d'espace.
 *
 * ⛔ **Ni couleur seule ni emoji** : la spec l'écrit, « le type d'espace est
 * ÉCRIT ». L'icône ACCOMPAGNE le libellé, elle ne le remplace jamais — sinon
 * l'information passe par le seul canal qu'un lecteur d'écran ne lit pas et
 * qu'un daltonien ne distingue pas.
 *
 * Elle vit ici, avec le libellé, pour la même raison que lui : **deux** écrans
 * montrent la même liste — la page de choix et le sélecteur de l'en-tête.
 * Séparées, elles finiraient par ne plus montrer le même espace sous le même
 * signe.
 *
 * Les quatre icônes sont celles que `PORT-005 §6` nomme, à la lettre :
 * `Building2` (client), `GraduationCap` (apprenant), `Presentation`
 * (intervenant), `User` (composite — c'est le particulier, dont l'espace agrège
 * son grant de contact et son grant d'apprenant).
 */
const ICONE_PAR_NATURE: Record<PortalContext['kind'], LucideIcon> = {
    CONTACT: Building2,
    INSTRUCTOR: Presentation,
    LEARNER: GraduationCap,
    PARTICULIER: User,
}

export function iconeDuTypeDEspace(kind: PortalContext['kind']): LucideIcon {
    return ICONE_PAR_NATURE[kind] ?? Building2
}

/**
 * PORT-005 §5.2 — **la ligne 2 d'un espace, telle qu'elle s'AFFICHE.**
 *
 * Le serveur sert `{pour qui} · {organisme}` ; l'écran y ajoute le RÉGIME d'une
 * structure sous-traitante (`instructor_scope === 'ORG'`), traduit. 🔴 Jusqu'au
 * 2026-09-16 le serveur collait « (structure) » en français, rendu tel quel dans
 * l'interface anglaise (arbitrage n° 11). ⚠️ Une seule fonction pour les TROIS
 * surfaces — page de choix, sélecteur, en-tête : posé dans deux sur trois, le régime
 * se lisait dans le menu et disparaissait de l'en-tête, à quarante pixels d'écart.
 */
export function ligneDeuxDuContexte(
    contexte: Pick<PortalContext, 'subtitle' | 'instructor_scope'>,
    t: TFunction,
): string {
    const regime = contexte.instructor_scope === 'ORG' ? t('portal.context.instructorScope.org') : ''
    return [contexte.subtitle, regime].filter(Boolean).join(' ')
}

/**
 * PORT-005 §5.2 (décision n° 5) — **« Dernière visite », toujours RELATIVE.**
 *
 * `formatTimelineAge` (`lib/date-utils.ts`), l'utilitaire des fils de vie : jamais
 * un ISO, et jamais le frère `formatRelativeTime`, qui rend `null` au-delà de sept
 * jours. ⚠️ Ses libellés commencent par une capitale (« Hier », « Il y a 3 mois ») :
 * placés après « Dernière visite : », ils passent en minuscule — le wireframe §5.1
 * écrit « hier ». Un espace jamais ouvert dit `portal.context.never` : c'est le cas
 * de toute invitation en attente.
 *
 * 🔴 **`null` sous l'espace EN COURS (A19, décision PO du 2026-09-16)** : `select` écrit
 * `last_access_at` à l'ouverture, et la ligne y disait presque toujours « à l'instant ».
 * La règle vit ICI pour les trois surfaces qui la rendent (page de choix, menu de bureau
 * et volet bas du sélecteur), avec le MÊME `actifId` que le tri (`ordreDesEspaces.ts`) —
 * l'ordre, lui, ne change pas : l'espace en cours reste en tête.
 */
export function derniereVisiteDuContexte(
    contexte: Pick<PortalContext, 'id' | 'last_access_at'>,
    actifId: string | null,
    t: TFunction,
): string | null {
    if (contexte.id === actifId) return null
    if (!contexte.last_access_at) return t('portal.context.lastVisit', { when: t('portal.context.never') })
    const { relative } = formatTimelineAge(contexte.last_access_at)
    // `toLowerCase` et non `toLocaleLowerCase(langue)` : une étiquette de langue
    // invalide y lève une `RangeError`, et les deux langues servies n'en ont pas besoin.
    return t('portal.context.lastVisit', { when: relative.charAt(0).toLowerCase() + relative.slice(1) })
}
